import { useCallback } from "react";

import type { MutationFunction } from "@tanstack/react-query";
import type { AxiosError, AxiosResponse } from "axios";

import { apiInstance } from "../api/api-client";
import type { useToastNotificationsProps } from "./useToastNotifications";
import { useToastNotifications } from "./useToastNotifications";
import { filterObjectKeys } from "../lib/utils";

interface useMutateProps extends useToastNotificationsProps {
  method: "post" | "put" | "patch" | "delete";
  url: string;
  apiVersion?: "v1" | "v2";
}

export function useMutate<TRequest>({
  method,
  url,
  toast,
  refetchQueries,
  apiVersion = "v1",
}: useMutateProps): {
  mutationFn: MutationFunction<AxiosResponse, TRequest>;
  onSuccess: () => void;
  onError: (error: AxiosError) => void;
} {
  const { onSuccess, onError } = useToastNotifications({
    toast,
    refetchQueries,
  });
  const useV2 = apiVersion === "v2";

  const mutationFn: MutationFunction<AxiosResponse, TRequest> = useCallback(
    async (data) => {
      const filteredData =
        typeof data === "object" && data !== null
          ? filterObjectKeys(data)
          : data;

      switch (method) {
        case "post":
          return apiInstance.post(url, filteredData, undefined, useV2);
        case "put":
          return apiInstance.put(url, filteredData, useV2);
        case "patch":
          return apiInstance.patch(url, filteredData, undefined, useV2);
        case "delete":
          return apiInstance.delete(url, useV2);
        default:
          throw new Error(`Unsupported mutation method: ${method}`);
      }
    },
    [method, url, useV2],
  );

  return { mutationFn, onSuccess, onError };
}
